import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { X, Loader2, UserCog } from 'lucide-react';
import { adminTicketsAPI, adminLookupAPI } from '../../services/api';

/**
 * Change a ticket's primary owner. Mirrors CW's Change Owner flow: pick a new
 * member, optionally notify them by email and attach a message that becomes an
 * internal note in CW.
 *
 * Pass currentOwner so the dialog can show who's being replaced (and disable
 * Save when the same member is reselected).
 */
export default function AdminChangeOwnerModal({ ticketId, currentOwner, onClose, onSuccess }) {
  const [memberId, setMemberId] = useState('');
  const [notify, setNotify] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState(null);

  const { data: membersData } = useQuery({
    queryKey: ['admin-lookup-members'],
    queryFn: () => adminLookupAPI.members(),
  });
  const members = membersData?.data?.items || [];

  const changeMutation = useMutation({
    mutationFn: (data) => adminTicketsAPI.changeOwner(ticketId, data),
    onSuccess: () => {
      onSuccess?.();
      onClose?.();
    },
    onError: (err) =>
      setError(err.response?.data?.error || err.response?.data?.details || 'Failed to change owner'),
  });

  const samePerson = currentOwner?.id && String(currentOwner.id) === String(memberId);
  const canSubmit = !!memberId && !samePerson && !changeMutation.isPending;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-700">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white flex items-center gap-2">
            <UserCog className="w-5 h-5" />
            Change Owner
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-neutral-400 hover:text-neutral-700 dark:hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          <div className="text-sm text-neutral-600 dark:text-neutral-300">
            Current owner: <strong>{currentOwner?.name || currentOwner?.identifier || 'Unassigned'}</strong>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1">
              New owner *
            </label>
            <select
              value={memberId}
              onChange={(e) => setMemberId(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 text-sm"
            >
              <option value="">Pick a CW member…</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
            {samePerson && (
              <div className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                That's already the owner.
              </div>
            )}
          </div>

          <div className="border-t border-neutral-200 dark:border-neutral-700 pt-3">
            <label className="flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-200">
              <input
                type="checkbox"
                checked={notify}
                onChange={(e) => setNotify(e.target.checked)}
              />
              Notify new owner by email
            </label>
            <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
              Triggers ConnectWise's notify-on-add for this ticket.
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1">
              Message (optional)
            </label>
            <textarea
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Add an internal note that goes out with the notification…"
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 text-sm"
            />
            <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
              If supplied, this becomes an internal note (visible to techs in CW, hidden from the
              customer portal).
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-neutral-200 dark:border-neutral-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700"
          >
            Cancel
          </button>
          <button
            disabled={!canSubmit}
            onClick={() =>
              changeMutation.mutate({ memberId: parseInt(memberId), notify, message })
            }
            className="px-4 py-2 text-sm bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 flex items-center gap-2"
          >
            {changeMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
